/**
 * Performance Targets — Analytics tab (IA-REORG, Jun 2026)
 * Single source of truth for target management.
 * Visible to Admin + Team Leaders only. TLs are scoped to their own team (server-enforced).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../App';
import {
  Target, Search, RefreshCw, Pencil, History, Power, PowerOff,
  CheckCircle2, AlertCircle, MinusCircle, Users, ShieldAlert
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../ui/dialog';
import { toast } from 'sonner';
import { formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const ALLOWED_ROLES = ['admin', 'team_leader', 'tax_search_team_leader', 'title_search_team_leader'];

const roleLabel = (r) => (r || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return formatDateTimeLocal(d);
  } catch {
    return '—';
  }
};

import { AnKpiCard, AnCard, AnStatusPill, AnAvatar, AnEmptyState } from '../analytics/ui/AnalyticsUi';

const StatusBadge = ({ status, source }) => {
  if (status === 'configured') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <AnStatusPill tone="green" dot testId="target-status-pill">Configured</AnStatusPill>
        {source && source !== 'user' && <span className="text-[10px] text-slate-400 uppercase">{source} default</span>}
      </span>
    );
  }
  if (status === 'inactive') return <AnStatusPill tone="slate" testId="target-status-pill">Inactive</AnStatusPill>;
  return <AnStatusPill tone="amber" dot testId="target-status-pill">Missing</AnStatusPill>;
};

const SummaryChip = ({ icon: Icon, label, value, tone }) => (
  <div className={`flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 bg-white ${tone}`}>
    <Icon className="h-4 w-4" />
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-base font-bold text-slate-900">{value}</span>
  </div>
);

const ACTION_LABEL = {
  created: 'Created',
  updated: 'Updated',
  deactivated: 'Deactivated',
  reactivated: 'Reactivated',
};

const ACTION_TONE = {
  created: 'bg-blue-100 text-blue-700',
  updated: 'bg-indigo-100 text-indigo-700',
  deactivated: 'bg-red-100 text-red-700',
  reactivated: 'bg-emerald-100 text-emerald-700',
};

export default function PerformanceTargets() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, configured: 0, missing: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Edit / create dialog
  const [editDialog, setEditDialog] = useState(null); // {row, daily, weekly, notes}
  const [saving, setSaving] = useState(false);

  // Deactivate / reactivate dialog
  const [statusDialog, setStatusDialog] = useState(null); // {row, activate, reason}
  const [statusSaving, setStatusSaving] = useState(false);

  // Audit history dialog
  const [historyDialog, setHistoryDialog] = useState(null); // {row, entries, loading}

  const allowed = ALLOWED_ROLES.includes(user?.role);

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/performance/targets/overview`);
      setRows(res.data.rows || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      console.error('Failed to load targets overview:', err);
      toast.error(err?.response?.data?.detail || 'Failed to load performance targets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) fetchOverview();
  }, [allowed, fetchOverview]);

  const openEdit = (row) => {
    setEditDialog({
      row,
      daily: row.source === 'user' && row.daily_target ? String(row.daily_target) : '',
      weekly: row.source === 'user' && row.weekly_target && row.weekly_target !== (row.daily_target || 0) * 5
        ? String(row.weekly_target) : '',
      notes: row.source === 'user' ? (row.notes || '') : '',
    });
  };

  const handleSave = async () => {
    const daily = parseInt(editDialog.daily, 10);
    if (!daily || daily < 1 || daily > 500) {
      toast.error('Daily target must be between 1 and 500');
      return;
    }
    const weekly = editDialog.weekly ? parseInt(editDialog.weekly, 10) : null;
    if (weekly !== null && (isNaN(weekly) || weekly < 1 || weekly > 2500)) {
      toast.error('Weekly target must be between 1 and 2500');
      return;
    }
    try {
      setSaving(true);
      await axios.post(`${API}/performance/targets`, {
        daily_target: daily,
        weekly_target: weekly,
        scope: 'user',
        user_id: editDialog.row.user_id,
        notes: editDialog.notes || null,
      });
      toast.success(`Target saved for ${editDialog.row.name}`);
      setEditDialog(null);
      fetchOverview();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save target');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async () => {
    if (!statusDialog.reason || statusDialog.reason.trim().length < 3) {
      toast.error('A reason is required (minimum 3 characters)');
      return;
    }
    try {
      setStatusSaving(true);
      await axios.patch(`${API}/performance/targets/${statusDialog.row.target_id}/status`, {
        active: statusDialog.activate,
        reason: statusDialog.reason.trim(),
      });
      toast.success(
        `Target ${statusDialog.activate ? 'reactivated' : 'deactivated'} for ${statusDialog.row.name}`
      );
      setStatusDialog(null);
      fetchOverview();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update target status');
    } finally {
      setStatusSaving(false);
    }
  };

  const openHistory = async (row) => {
    setHistoryDialog({ row, entries: [], loading: true });
    try {
      const res = await axios.get(`${API}/performance/targets/audit`, {
        params: { user_id: row.user_id, limit: 100 },
      });
      setHistoryDialog({ row, entries: res.data.entries || [], loading: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load audit history');
      setHistoryDialog({ row, entries: [], loading: false });
    }
  };

  if (!allowed) {
    return (
      <Card data-testid="targets-access-denied">
        <CardContent className="p-12 text-center">
          <ShieldAlert className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Restricted</h3>
          <p className="text-slate-500">Performance Targets are managed by Admins and Team Leaders only.</p>
        </CardContent>
      </Card>
    );
  }

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.name?.toLowerCase().includes(q) ||
      r.email?.toLowerCase().includes(q) ||
      r.team_name?.toLowerCase().includes(q) ||
      roleLabel(r.role).toLowerCase().includes(q) ||
      r.status?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-5" data-testid="performance-targets-page">
      {/* Summary KPI cards + search (same summary values, same search/refresh behaviour) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="targets-summary">
        <AnKpiCard compact icon={Users} tone="slate" label="Users" value={summary.total ?? 0} testid="targets-kpi-users" delay={0} />
        <AnKpiCard compact icon={CheckCircle2} tone="green" label="Configured" value={summary.configured ?? 0} testid="targets-kpi-configured" delay={50} />
        <AnKpiCard compact icon={AlertCircle} tone="amber" label="Missing" value={summary.missing ?? 0} testid="targets-kpi-missing" delay={100} />
        <AnKpiCard compact icon={MinusCircle} tone="slate" label="Inactive" value={summary.inactive ?? 0} testid="targets-kpi-inactive" delay={150} />
      </div>
      <AnCard className="px-3 py-2.5 flex items-center gap-2 flex-wrap" testId="targets-toolbar">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Search user, team, status…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 rounded-lg text-[13px]"
            data-testid="targets-search-input"
          />
        </div>
        <Button variant="outline" size="sm" onClick={fetchOverview} disabled={loading} data-testid="targets-refresh-btn" className="an-btn h-9 rounded-lg ml-auto">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </AnCard>

      {/* Data table */}
      <Card className="an-rise overflow-hidden rounded-2xl border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (
            <Table data-testid="targets-table">
              <TableHeader>
                <TableRow className="an-thead hover:bg-transparent border-0">
                  <TableHead className="!text-white">User</TableHead>
                  <TableHead className="!text-white">Team</TableHead>
                  <TableHead className="!text-white">Role</TableHead>
                  <TableHead className="!text-white text-center">Daily Target</TableHead>
                  <TableHead className="!text-white text-center">Weekly Target</TableHead>
                  <TableHead className="!text-white">Status</TableHead>
                  <TableHead className="!text-white">Last Updated</TableHead>
                  <TableHead className="!text-white">Updated By</TableHead>
                  <TableHead className="!text-white text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="p-3">
                      <AnEmptyState tone="neutral" icon={<Search className="h-5 w-5" />} title="No users match your search." subtitle="Try a different name, team or status." testId="targets-empty" />
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((row) => {
                  const inactive = row.status === 'inactive';
                  const hasUserTarget = row.source === 'user' && row.target_id;
                  return (
                    <TableRow key={row.user_id} data-testid={`target-row-${row.user_id}`} className={`an-row ${inactive ? 'opacity-70' : ''}`}>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <AnAvatar name={row.name} tone={inactive ? 'indigo' : 'sky'} />
                          <div>
                            <p className="text-[13px] font-semibold text-slate-900">{row.name}</p>
                            <p className="text-[11px] text-slate-400">{row.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{row.team_name || '—'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{roleLabel(row.role)}</TableCell>
                      <TableCell className="text-center">
                        <span className={`text-sm font-bold ${inactive ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                          {row.daily_target ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`text-sm ${inactive ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                          {row.weekly_target ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell><StatusBadge status={row.status} source={row.source} /></TableCell>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(row.last_updated)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{row.updated_by_name || '—'}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="sm"
                            className="h-8 px-2 text-slate-500 hover:text-blue-600"
                            title={row.status === 'missing' ? 'Set target' : 'Edit target'}
                            onClick={() => openEdit(row)}
                            data-testid={`target-edit-btn-${row.user_id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {hasUserTarget && !inactive && (
                            <Button
                              variant="ghost" size="sm"
                              className="h-8 px-2 text-slate-500 hover:text-red-600"
                              title="Deactivate target"
                              onClick={() => setStatusDialog({ row, activate: false, reason: '' })}
                              data-testid={`target-deactivate-btn-${row.user_id}`}
                            >
                              <PowerOff className="h-4 w-4" />
                            </Button>
                          )}
                          {hasUserTarget && inactive && (
                            <Button
                              variant="ghost" size="sm"
                              className="h-8 px-2 text-slate-500 hover:text-emerald-600"
                              title="Reactivate target"
                              onClick={() => setStatusDialog({ row, activate: true, reason: '' })}
                              data-testid={`target-reactivate-btn-${row.user_id}`}
                            >
                              <Power className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost" size="sm"
                            className="h-8 px-2 text-slate-500 hover:text-indigo-600"
                            title="Change history"
                            onClick={() => openHistory(row)}
                            data-testid={`target-history-btn-${row.user_id}`}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400">
        Inactive targets are excluded from target-based KPI calculations. Users showing “Missing” have no
        target configured at any level (user, role, or global).
      </p>

      {/* Edit / Create dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => { if (!o && !saving) setEditDialog(null); }}>
        <DialogContent data-testid="target-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-600" />
              {editDialog?.row?.status === 'missing' ? 'Set Target' : 'Edit Target'} — {editDialog?.row?.name}
            </DialogTitle>
            <DialogDescription>
              {roleLabel(editDialog?.row?.role)}{editDialog?.row?.team_name ? ` • ${editDialog.row.team_name}` : ''}.
              Changes are recorded in the audit history.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Daily Target *</label>
              <Input
                type="number" min={1} max={500}
                value={editDialog?.daily ?? ''}
                onChange={(e) => setEditDialog((d) => ({ ...d, daily: e.target.value }))}
                data-testid="target-daily-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Weekly Target (optional)</label>
              <Input
                type="number" min={1} max={2500}
                placeholder="Auto: daily × 5"
                value={editDialog?.weekly ?? ''}
                onChange={(e) => setEditDialog((d) => ({ ...d, weekly: e.target.value }))}
                data-testid="target-weekly-input"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Notes (optional)</label>
              <Input
                type="text" placeholder="e.g., High performer"
                value={editDialog?.notes ?? ''}
                onChange={(e) => setEditDialog((d) => ({ ...d, notes: e.target.value }))}
                data-testid="target-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)} disabled={saving} data-testid="target-edit-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="target-save-btn">
              {saving ? 'Saving…' : 'Save Target'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate / Reactivate dialog */}
      <Dialog open={!!statusDialog} onOpenChange={(o) => { if (!o && !statusSaving) setStatusDialog(null); }}>
        <DialogContent data-testid="target-status-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {statusDialog?.activate
                ? <Power className="h-5 w-5 text-emerald-600" />
                : <PowerOff className="h-5 w-5 text-red-600" />}
              {statusDialog?.activate ? 'Reactivate' : 'Deactivate'} Target — {statusDialog?.row?.name}
            </DialogTitle>
            <DialogDescription>
              {statusDialog?.activate
                ? 'The target will be re-included in target-based KPI calculations.'
                : 'The target will be excluded from target-based KPI calculations and shown as Inactive (not Missing).'}
              {' '}A reason is required and will be recorded in the audit history.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Reason *</label>
            <Textarea
              placeholder={statusDialog?.activate ? 'e.g., Returning from leave' : 'e.g., Extended leave of absence'}
              value={statusDialog?.reason ?? ''}
              onChange={(e) => setStatusDialog((d) => ({ ...d, reason: e.target.value }))}
              rows={3}
              data-testid="target-status-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)} disabled={statusSaving} data-testid="target-status-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleStatusChange}
              disabled={statusSaving || !statusDialog?.reason?.trim() || statusDialog.reason.trim().length < 3}
              className={statusDialog?.activate ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="target-status-confirm-btn"
            >
              {statusSaving ? 'Saving…' : statusDialog?.activate ? 'Reactivate' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit history dialog */}
      <Dialog open={!!historyDialog} onOpenChange={(o) => { if (!o) setHistoryDialog(null); }}>
        <DialogContent className="max-w-2xl" data-testid="target-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-600" />
              Target History — {historyDialog?.row?.name}
            </DialogTitle>
            <DialogDescription>All target changes, newest first.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto space-y-3 pr-1">
            {historyDialog?.loading && (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            )}
            {!historyDialog?.loading && historyDialog?.entries?.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-8" data-testid="target-history-empty">
                No target changes recorded for this user yet.
              </p>
            )}
            {!historyDialog?.loading && historyDialog?.entries?.map((e) => (
              <div key={e.id} className="border border-slate-200 rounded-lg p-3" data-testid={`audit-entry-${e.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${ACTION_TONE[e.action] || 'bg-slate-100 text-slate-600'}`}>
                    {ACTION_LABEL[e.action] || e.action}
                  </span>
                  <span className="text-xs text-slate-400">{fmtDate(e.timestamp)}</span>
                </div>
                <div className="text-sm text-slate-700">
                  <span className="text-slate-400">{e.previous_value}</span>
                  <span className="mx-1.5 text-slate-300">→</span>
                  <span className="font-medium">{e.new_value}</span>
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-xs text-slate-500">
                    {e.reason ? <>Reason: <span className="italic">{e.reason}</span></> : null}
                  </p>
                  <p className="text-xs text-slate-400">by {e.updated_by_name || '—'}</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
